import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiService } from './api.service';
import { StorageService } from '../shared/storage.service';
import { STORAGE_KEYS } from '../utils/constants';
import { environment } from '../../environments/environment';

export interface BankExtractionDocument {
  documentName?: string;
  documentUrl: string;
  documentType: string;
}

export interface CreateBankExtractionRequestDto {
  bankBeneficiaryId: string;
  accountNumber: string;
  amount: number;
  currencyId: number;
  paymentType: 'total' | 'partial';
  billPercent?: number;
  importerId: string;
  paymentDocumentType: string;
  documentNumber?: string;
  documents: BankExtractionDocument[];
  paymentReasonId: string;
  description: string;
  twoFactorCode?: string; // Two-factor authentication code
}

export interface UpdateBankExtractionRequestDto {
  status?: string;
  accountNumber?: string;
  amount?: number;
  currencyId?: number;
  paymentType?: 'total' | 'partial';
  billPercent?: number;
  paymentDocumentType?: string;
  documentNumber?: string;
  documents?: BankExtractionDocument[];
  paymentReasonId?: string;
  description?: string;
}

export interface Comment {
  userId: string;
  userFullName: string;
  isAdmin: boolean;
  date: Date;
  text: string;
  visibleToUser: boolean;
  readed?: boolean;
}

export interface StatusChange {
  _id: string;
  id: string;
  previousStatus: string;
  newStatus: string;
  changedBy: string;
  changedAt: string;
  changedByUserName?: string;
}

export interface BankExtractionRequest {
  _id: string;
  sequentialNumber?: number;
  paymentRequestNumber?: string;
  bankBeneficiaryId: string;
  accountNumber: string;
  amount: number;
  currencyId: number;
  paymentType: 'total' | 'partial';
  billPercent?: number;
  importerId: string;
  paymentDocumentType: string;
  documentNumber?: string;
  documents: BankExtractionDocument[];
  paymentReasonId: string;
  description: string;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  comments?: Comment[];
  statusHistory?: StatusChange[];
  // Populated fields
  bankBeneficiary?: any;
  currency?: any;
  paymentReason?: any;
  importer?: any;
}

@Injectable({
  providedIn: 'root'
})
export class BankExtractionRequestService {
  private http = inject(HttpClient);
  private storageService = inject(StorageService);

  constructor(private apiService: ApiService) {}

  /**
   * Create a new bank extraction request
   */
  async createBankExtractionRequest(data: CreateBankExtractionRequestDto) {
    return this.apiService.post('v1/bank-extraction-request/create', { body: data });
  }

  /**
   * Get all bank extraction requests with pagination
   */
  async getBankExtractionRequests(pagination: { page: number; limit: number }, filter: any = {}) {
    return this.apiService.post('v1/bank-extraction-request/customer-bank-extraction-requests', {
      body: {
        pagination,
        dto: filter
      }
    });
  }

  /**
   * Get a specific bank extraction request by ID
   */
  async getBankExtractionRequestById(id: string) {
    return this.apiService.get(`v1/bank-extraction-request/${id}`);
  }

  /**
   * Update a bank extraction request
   */
  async updateBankExtractionRequest(id: string, data: UpdateBankExtractionRequestDto) {
    return this.apiService.patch(`v1/bank-extraction-request/${id}`, { body: data });
  }

  /**
   * Delete a bank extraction request
   */
  async deleteBankExtractionRequest(id: string) {
    return this.apiService.delete(`v1/bank-extraction-request/${id}`);
  }

  /**
   * Cancel a bank extraction request
   */
  async cancelBankExtractionRequest(id: string) {
    return this.apiService.post(`v1/bank-extraction-request/${id}/cancel`, {});
  }

  /**
   * Add user comment to bank extraction request
   */
  async addUserComment(id: string, text: string) {
    return this.apiService.post(`v1/bank-extraction-request/${id}/comment/user`, {
      body: { text }
    });
  }

  /**
   * Upload document for bank extraction request using multipart/form-data
   */
  async uploadDocument(
    file: File
  ): Promise<{
    data?: { documentUrl: string; fileName?: string; originalName?: string; size?: number };
    error?: any;
    errorShowed?: boolean;
  }> {
    try {
      // Crear FormData para enviar el archivo
      const formData = new FormData();
      formData.append('file', file);

      // Enviar usando FormData (multipart/form-data)
      const response = await this.apiService.post('v1/bank-extraction-request/upload-invoice', {
        body: formData
      });
      
      return response;
    } catch (error) {
      console.error('Upload error:', error);
      return { error };
    }
  }

  /**
   * Download document from bank extraction request
   * Calls the backend which in turn calls Account Manager microservice to get the file from Minio
   */
  async downloadDocument(documentUrl: string): Promise<{ data?: Blob; error?: any }> {
    try {
      const url = `${environment.api_url}/v1/bank-extraction-request/download-document`;
      // Modo cookie (Fase 2): auth por cookie httpOnly (credentials:'include'), sin header
      // Authorization; se refleja el token CSRF. Modo header: como antes.
      const cookieMode = environment.useCookieAuth;
      const authHeaders: Record<string, string> = cookieMode
        ? (() => {
            const m = document.cookie.match(/(?:^|;\s*)sp_csrf=([^;]+)/);
            return m ? { 'X-CSRF-Token': decodeURIComponent(m[1]) } : {};
          })()
        : { Authorization: `Bearer ${this.storageService.get(STORAGE_KEYS.AUTH_TOKEN)}` };

      const response = await fetch(url, {
        method: 'POST',
        credentials: cookieMode ? 'include' : 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ documentUrl })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      return { data: blob };
    } catch (error) {
      console.error('Download error:', error);
      return { error };
    }
  }
}
